import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, apiDownload } from '../api/client.js';
import type {
  ApprovalReportRow,
  FacilityOption,
  FinancialSummaryRow,
  ReportColumn,
  WorkOrderReport,
} from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { WORK_ORDER_STATUS_LABELS } from '../../shared/workflow.js';
import type { WorkOrderStatus } from '../../shared/workflow.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextInput,
} from '../components/ui.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import { summarize } from '../reports/cycleTime.js';
import type { CycleLeg } from '../reports/cycleTime.js';

type ReportTab = 'work-orders' | 'approvals';

export function ReportsPage() {
  const { can } = useSession();
  const [tab, setTab] = useState<ReportTab>('work-orders');
  const [filters, setFilters] = useState({ from: '', to: '', facilityId: '' });
  const [downloading, setDownloading] = useState(false);
  // Empty means every column, which is also what the API does with no `columns`.
  const [chosenColumns, setChosenColumns] = useState<string[]>([]);

  const showFinancials = can(PERMISSIONS.REPORT_FINANCIAL_VIEW);

  const activeFilters = {
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.facilityId ? { facilityId: filters.facilityId } : {}),
  };

  const { data: facilities } = useQuery({
    queryKey: ['reference', 'facilities'],
    queryFn: () =>
      apiRequest<{ facilities: FacilityOption[] }>('/api/reference/facilities').then(
        (result) => result.facilities,
      ),
  });

  const { data: approvals, isLoading: approvalsLoading } = useQuery({
    queryKey: ['reports', 'approvals', activeFilters],
    queryFn: () =>
      apiRequest<{ rows: ApprovalReportRow[] }>('/api/reports/approvals', {
        query: activeFilters,
      }).then((result) => result.rows),
    enabled: tab === 'approvals',
  });

  const workOrderQuery = {
    ...activeFilters,
    ...(chosenColumns.length > 0 ? { columns: chosenColumns.join(',') } : {}),
  };

  const { data: availableColumns } = useQuery({
    queryKey: ['reports', 'work-order-columns'],
    queryFn: () =>
      apiRequest<{ columns: ReportColumn[] }>('/api/reports/work-order-columns').then(
        (result) => result.columns,
      ),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'work-orders', workOrderQuery],
    queryFn: () =>
      apiRequest<WorkOrderReport>('/api/reports/work-orders', { query: workOrderQuery }),
  });

  const { data: financial } = useQuery({
    queryKey: ['reports', 'financial-summary'],
    queryFn: () =>
      apiRequest<{ rows: FinancialSummaryRow[] }>('/api/reports/financial-summary').then(
        (result) => result.rows,
      ),
    enabled: showFinancials,
  });

  // The detailed report is a document rather than a grid, so it opens on its own page
  // carrying the same filters and reports every field regardless of the column choice.
  const detailedReportSearch = new URLSearchParams(activeFilters).toString();
  const detailedReportQuery = detailedReportSearch === '' ? '' : `?${detailedReportSearch}`;

  const handleExport = async () => {
    setDownloading(true);
    try {
      const path = tab === 'approvals' ? '/api/reports/approvals' : '/api/reports/work-orders';
      const query = tab === 'approvals' ? activeFilters : workOrderQuery;
      await apiDownload(path, `${tab}.csv`, { ...query, format: 'csv' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle={
          tab === 'approvals'
            ? 'Every approval decision within your facility scope, for the monthly invoice backup required by the agreement.'
            : 'Work order activity and cycle times within your facility scope.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {tab === 'work-orders' && (
              <Link to={`/reports/detailed${detailedReportQuery}`}>
                <Button variant="secondary">Detailed report (Word or PDF)</Button>
              </Link>
            )}
            <Button variant="secondary" onClick={() => void handleExport()} loading={downloading}>
              Export CSV
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex gap-2">
        <Button
          variant={tab === 'work-orders' ? 'primary' : 'secondary'}
          onClick={() => setTab('work-orders')}
        >
          Work orders
        </Button>
        <Button
          variant={tab === 'approvals' ? 'primary' : 'secondary'}
          onClick={() => setTab('approvals')}
        >
          Approvals
        </Button>
      </div>

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <Field label={tab === 'approvals' ? 'Decided from' : 'Submitted from'} htmlFor="from">
            <TextInput
              id="from"
              type="date"
              value={filters.from}
              onChange={(event) => setFilters({ ...filters, from: event.target.value })}
            />
          </Field>
          <Field label={tab === 'approvals' ? 'Decided to' : 'Submitted to'} htmlFor="to">
            <TextInput
              id="to"
              type="date"
              value={filters.to}
              onChange={(event) => setFilters({ ...filters, to: event.target.value })}
            />
          </Field>
          <Field label="Facility" htmlFor="reportFacility">
            <Select
              id="reportFacility"
              value={filters.facilityId}
              onChange={(event) => setFilters({ ...filters, facilityId: event.target.value })}
            >
              <option value="">All facilities</option>
              {(facilities ?? []).map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.facilityCode} — {facility.name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-end">
            <Button
              variant="ghost"
              onClick={() => setFilters({ from: '', to: '', facilityId: '' })}
            >
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {tab === 'approvals' ? (
        <ApprovalsTable rows={approvals ?? []} isLoading={approvalsLoading} />
      ) : (
        <WorkOrdersReport
          report={data ?? { columns: [], rows: [] }}
          isLoading={isLoading}
          financial={financial ?? []}
          showFinancials={showFinancials}
          availableColumns={availableColumns ?? []}
          chosenColumns={chosenColumns}
          onChooseColumns={setChosenColumns}
        />
      )}
    </>
  );
}

function WorkOrdersReport({
  report,
  isLoading,
  financial,
  showFinancials,
  availableColumns,
  chosenColumns,
  onChooseColumns,
}: {
  report: WorkOrderReport;
  isLoading: boolean;
  financial: FinancialSummaryRow[];
  showFinancials: boolean;
  availableColumns: ReportColumn[];
  chosenColumns: string[];
  onChooseColumns: (keys: string[]) => void;
}) {
  const data = report.rows;
  const summary = summarize(data);

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3">
        <Metric label="Work orders" value={String(data.length)} />
        {showFinancials && (
          <Metric label="Approved value" value={formatCurrency(summary.approvedTotalCents)} />
        )}
      </div>

      <CycleTimes legs={summary.legs} />

      {showFinancials && financial.length > 0 && (
        <Card title="Financial summary by task order and facility" className="mb-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Approved value by task order and facility</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    Task order
                  </th>
                  <th scope="col" className="table-header-cell">
                    Facility
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Approved
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Approved value
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Markup
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {financial?.map((row) => (
                  <tr key={`${row.taskOrderNumber}-${row.facilityCode}`}>
                    <td className="table-cell-base">{row.taskOrderNumber}</td>
                    <td className="table-cell-base">{row.facilityCode}</td>
                    <td className="table-cell-base text-right tabular-nums">{row.approvedCount}</td>
                    <td className="table-cell-base text-right tabular-nums">
                      {formatCurrency(row.approvedTotalCents)}
                    </td>
                    <td className="table-cell-base text-right tabular-nums">
                      {formatCurrency(row.markupTotalCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ColumnPicker
        available={availableColumns}
        chosen={chosenColumns}
        onChange={onChooseColumns}
      />

      <Card title="Work orders" bodyClassName="px-0 py-0 sm:px-0">
        {isLoading ? (
          <div className="p-4">
            <Spinner label="Running report" />
          </div>
        ) : data.length === 0 ? (
          <EmptyState title="No work orders in this range" description="Widen the date range." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Work order report results</caption>
              <thead className="bg-slate-50">
                <tr>
                  {report.columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={`table-header-cell${isNumericColumn(column.key) ? ' text-right' : ''}`}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map((row, rowIndex) => (
                  <tr key={String(row.workOrderNumber ?? rowIndex)}>
                    {report.columns.map((column) => (
                      <td
                        key={column.key}
                        className={`table-cell-base${
                          isNumericColumn(column.key) ? ' text-right tabular-nums' : ''
                        }${column.key === 'workOrderNumber' ? ' font-mono text-xs' : ''}`}
                      >
                        {formatReportCell(column.key, row[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

const DECISION_LABELS: Record<string, string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  revision_requested: 'Revision requested',
};

/**
 * The invoice backup view. Every column here is customer-facing: the amount is the
 * total USPS authorized, and the authority columns are the snapshot taken at the
 * moment of the decision rather than today's configuration.
 */
function ApprovalsTable({ rows, isLoading }: { rows: ApprovalReportRow[]; isLoading: boolean }) {
  const authorized = rows
    .filter((row) => row.decision === 'approved')
    .reduce((sum, row) => sum + (row.amountAuthorizedCents ?? 0), 0);

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Metric label="Decisions" value={String(rows.length)} />
        <Metric
          label="Approved"
          value={String(rows.filter((row) => row.decision === 'approved').length)}
        />
        <Metric label="Amount authorized" value={formatCurrency(authorized)} />
      </div>

      <Card title="Approvals" bodyClassName="px-0 py-0 sm:px-0">
        {isLoading ? (
          <div className="p-4">
            <Spinner label="Running report" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No approval decisions in this range"
            description="Widen the date range, or check the facility filter."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Approval decisions for invoice backup</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    Work order
                  </th>
                  <th scope="col" className="table-header-cell">
                    Facility
                  </th>
                  <th scope="col" className="table-header-cell">
                    Decision
                  </th>
                  <th scope="col" className="table-header-cell">
                    Decided
                  </th>
                  <th scope="col" className="table-header-cell">
                    Decided by
                  </th>
                  <th scope="col" className="table-header-cell">
                    Authority
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Amount authorized
                  </th>
                  <th scope="col" className="table-header-cell">
                    Response Line
                  </th>
                  <th scope="col" className="table-header-cell text-right">
                    Quotes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={`${row.workOrderNumber}-${row.versionNo}-${row.decidedAt}`}>
                    <td className="table-cell-base font-mono text-xs">
                      {row.workOrderNumber}
                      <span className="ml-1 text-slate-500">v{row.versionNo}</span>
                    </td>
                    <td className="table-cell-base">{row.facilityCode}</td>
                    <td className="table-cell-base">
                      {DECISION_LABELS[row.decision] ?? row.decision}
                    </td>
                    <td className="table-cell-base">{formatDate(row.decidedAt)}</td>
                    <td className="table-cell-base">{row.deciderName}</td>
                    <td className="table-cell-base">{row.authorityBasis ?? row.authorityLabel}</td>
                    <td className="table-cell-base text-right tabular-nums">
                      {row.amountAuthorizedCents === null
                        ? '—'
                        : formatCurrency(row.amountAuthorizedCents)}
                    </td>
                    <td className="table-cell-base">
                      {row.responseLineTicket
                        ? `${row.responseLineTicket}${row.coDiscretionApproved ? ' · CO discretion' : ''}`
                        : '—'}
                    </td>
                    <td className="table-cell-base text-right tabular-nums">
                      {row.quotesOnFile}
                      {row.competitionExceptionStatus === 'approved' ? ' (exception)' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

const NUMERIC_COLUMNS = new Set(['projectCostCents', 'daysToApproval', 'daysToCompletion']);
const DATE_COLUMNS = new Set([
  'submittedAt',
  'approvedAt',
  'performanceStartDate',
  'completedAt',
  'closedAt',
]);

function isNumericColumn(key: string): boolean {
  return NUMERIC_COLUMNS.has(key);
}

function formatReportCell(key: string, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'projectCostCents') return formatCurrency(Number(value));
  if (key === 'status') {
    return WORK_ORDER_STATUS_LABELS[String(value) as WorkOrderStatus] ?? String(value);
  }
  if (DATE_COLUMNS.has(key)) return formatDate(String(value));
  return String(value);
}

/**
 * The Contracting Officer may ask for the data in Postal Service fields, so the
 * columns are chosen here and the same choice drives the table and the CSV export.
 * Choosing none reports every column.
 */
function ColumnPicker({
  available,
  chosen,
  onChange,
}: {
  available: ReportColumn[];
  chosen: string[];
  onChange: (keys: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  if (available.length === 0) return null;

  const allKeys = available.map((column) => column.key);

  const toggle = (key: string) => {
    const effective = chosen.length === 0 ? allKeys : chosen;
    const next = effective.includes(key)
      ? effective.filter((candidate) => candidate !== key)
      : allKeys.filter((candidate) => effective.includes(candidate) || candidate === key);
    // A report of no columns is not a report, so the last one cannot be cleared.
    if (next.length === 0) return;
    onChange(next.length === allKeys.length ? [] : next);
  };

  return (
    <Card className="mb-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          {chosen.length === 0
            ? `Reporting all ${available.length} columns.`
            : `Reporting ${chosen.length} of ${available.length} columns.`}
        </p>
        <div className="flex gap-2">
          {chosen.length > 0 && (
            <Button variant="ghost" onClick={() => onChange([])}>
              Reset
            </Button>
          )}
          <Button variant="secondary" onClick={() => setOpen((previous) => !previous)}>
            {open ? 'Done' : 'Choose columns'}
          </Button>
        </div>
      </div>

      {open && (
        <fieldset className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <legend className="sr-only">Report columns</legend>
          {available.map((column) => (
            <label key={column.key} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={chosen.length === 0 || chosen.includes(column.key)}
                onChange={() => toggle(column.key)}
              />
              {column.header}
            </label>
          ))}
        </fieldset>
      )}
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

/**
 * Cycle time is two different measurements that a pair of similarly-worded cards
 * invited the reader to conflate, so it is drawn as the pipeline it describes:
 * each leg states what it measures, over how many work orders, and out of how
 * many that could have reached it.
 */
function CycleTimes({ legs }: { legs: readonly CycleLeg[] }) {
  return (
    <Card title="Cycle time" className="mb-4">
      <ol className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        {legs.map((leg) => (
          <li
            key={leg.label}
            className="flex-1 rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200"
            aria-label={`${leg.from} to ${leg.to}`}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {leg.from} <span aria-hidden="true">→</span> {leg.to}
            </p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-slate-900">
              {leg.average === null ? '—' : `${leg.average.toFixed(1)} days`}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {leg.count === 0
                ? `None of ${leg.eligible} yet`
                : `Median ${leg.median?.toFixed(1) ?? '—'} · ${leg.count} of ${leg.eligible}`}
            </p>
            {leg.excluded > 0 && (
              <p className="mt-1 text-xs text-amber-700">
                {leg.excluded} excluded: {leg.to.toLowerCase()} recorded before{' '}
                {leg.from.toLowerCase()}
              </p>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
